import { Divider } from 'antd';
import { useFela } from 'react-fela';
import { FormattedMessage, FormattedNumber, type FormatNumberOptions } from 'react-intl';

import { PricingModel, type AdminPricingSettings } from '@/modules/api/codegen';
import { convertCentsToDollars } from '@/modules/settings/utils';
import { Heading, Text, Tooltip } from '@/modules/ui';
import { InfoOutlined } from '@/modules/ui/components/Icon/InfoOutlined';

import * as felaRules from './BillingSection.rules';

export interface BillingSectionProps {
    adminPricingSettings: AdminPricingSettings;
}

export const BillingSection = ({ adminPricingSettings }: BillingSectionProps) => {
    const { css } = useFela();

    const { pricing } = adminPricingSettings;
    const pricingMode = pricing?.pricingModel === PricingModel.workspace ? 'perWorkspace' : 'perUser';

    const showUserCountSyncTooltip =
        pricing?.pricingModel === PricingModel.perUser &&
        typeof pricing.userCount === 'number' &&
        typeof adminPricingSettings.activeUserCount === 'number' &&
        pricing.userCount !== adminPricingSettings.activeUserCount;

    const currencyFormatParams = {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        style: 'currency',
        currency: pricing?.currency?.toUpperCase() ?? 'USD',
    } satisfies FormatNumberOptions;

    const basePrice =
        pricing?.pricingModel === PricingModel.perUser
            ? convertCentsToDollars(pricing?.pricePerUser ?? 0)
            : convertCentsToDollars(pricing?.expectedMonthlyBill ?? 0);

    return (
        <div className={css(felaRules.container)}>
            <Heading variant='h3' elementType='h2' color='foregroundsPrimary' customStyle={felaRules.billingTitle}>
                <FormattedMessage
                    id='settings.order.billing.title'
                    values={{
                        period: (
                            <span className={css(felaRules.interval)}>
                                (
                                <FormattedMessage
                                    id={`settings.order.billing.period.${adminPricingSettings.pricing?.interval ?? 'month'}`}
                                />
                                )
                            </span>
                        ),
                    }}
                />
            </Heading>

            <div className={css(felaRules.billingRow)}>
                <Text variant='ParagraphSmallMedium'>
                    <FormattedMessage id={`settings.order.billing.${pricingMode}.price.label`} />
                </Text>
                <Text variant='ParagraphSmallMedium' customStyle={felaRules.billingValueContainer}>
                    <FormattedMessage
                        id={`settings.order.billing.${pricingMode}.price.value`}
                        values={{
                            price: (
                                <span className={css(felaRules.billingValue)} data-testid='billing-base-price'>
                                    <FormattedNumber value={basePrice} {...currencyFormatParams} />
                                </span>
                            ),
                        }}
                    />
                </Text>
            </div>

            <div className={css(felaRules.billingRow)}>
                <Text variant='ParagraphSmallMedium'>
                    <FormattedMessage id={`settings.order.billing.${pricingMode}.tier.label`} />
                </Text>
                <Text variant='ParagraphSmallMedium' customStyle={felaRules.billingValueContainer}>
                    <FormattedMessage
                        id={`settings.order.billing.${pricingMode}.tier.value`}
                        values={{
                            users: (
                                <span className={css(felaRules.billingValue)} data-testid='billing-user-count'>
                                    {/* Per workspace tier name -> per user mode users count -> starter tier workspace active users count */}
                                    {pricing?.currentTier?.name ?? pricing?.userCount ?? adminPricingSettings.activeUserCount}
                                </span>
                            ),
                        }}
                    />
                    {showUserCountSyncTooltip && (
                        <Tooltip trigger='hover' title={<FormattedMessage id='settings.order.billing.perUser.tier.tooltip' />}>
                            <span className={css(felaRules.tooltipButton)}>
                                <InfoOutlined />
                            </span>
                        </Tooltip>
                    )}
                </Text>
            </div>

            <Divider className={css(felaRules.divider)} />

            <div className={css(felaRules.billingRow)}>
                <Text variant='ParagraphSmallMedium'>
                    <FormattedMessage id={`settings.order.billing.${pricingMode}.totalPrice.label`} />
                </Text>
                <Text variant='ParagraphSmallMedium' customStyle={felaRules.billingValueContainer}>
                    <FormattedMessage
                        id={`settings.order.billing.${pricingMode}.totalPrice.value`}
                        values={{
                            totalPrice: (
                                <span className={css(felaRules.totalPrice)} data-testid='billing-total-price'>
                                    <FormattedNumber
                                        value={convertCentsToDollars(pricing?.expectedMonthlyBill ?? 0)}
                                        {...currencyFormatParams}
                                    />
                                </span>
                            ),
                        }}
                    />
                </Text>
            </div>
        </div>
    );
};
